import React, { useState } from 'react';
import './FAQs.css';

function FAQs() {
  const [activeCategory, setActiveCategory] = useState('general');
  const [openQuestions, setOpenQuestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'general', name: 'General Questions', icon: '❓' },
    { id: 'products', name: 'Products & Sizing', icon: '👕' },
    { id: 'ordering', name: 'Ordering & Shipping', icon: '🛒' },
    { id: 'ndis', name: 'NDIS & Funding', icon: '💰' },
    { id: 'care', name: 'Care & Maintenance', icon: '🧼' },
    { id: 'returns', name: 'Returns & Exchanges', icon: '🔄' },
    { id: 'wholesale', name: 'Wholesale & Stockists', icon: '🏢' },
    { id: 'technical', name: 'Technical & Adaptive Features', icon: '⚙️' }
  ];

  const allFAQs = {
    general: [
      {
        id: 'gen-1',
        question: 'What makes The Shapes United different from other clothing brands?',
        answer: 'The Shapes United specializes in adaptive and sensory-friendly clothing designed for individuals with diverse needs. Our clothing features innovative designs like magnetic closures, open-back designs, tagless labels, and sensory-friendly fabrics that make dressing easier and more comfortable. We combine fashion-forward styles with functional adaptations that support independence and comfort.'
      },
      {
        id: 'gen-2',
        question: 'Where are your products manufactured?',
        answer: 'Our products are ethically manufactured in facilities that meet our strict standards for quality, worker safety, and environmental responsibility. We work with partners in Australia and overseas who share our commitment to fair labor practices and sustainable production. All our manufacturing partners are carefully vetted and regularly audited.'
      },
      {
        id: 'gen-3',
        question: 'Do you offer international shipping?',
        answer: 'Yes, we ship to most countries worldwide. Shipping costs and delivery times vary depending on your location. During checkout, you can select your country to see shipping options and estimated delivery times. Please note that some countries may have import duties or taxes that are the responsibility of the recipient.'
      },
      {
        id: 'gen-4',
        question: 'How can I stay updated on new products and promotions?',
        answer: 'You can stay updated by subscribing to our newsletter, following us on social media (@theshapesunited), or checking our website regularly. Newsletter subscribers receive exclusive offers, new product announcements, and helpful tips about adaptive clothing.'
      },
      {
        id: 'gen-5',
        question: 'Do you have physical stores where I can try items?',
        answer: 'We have partner stockists in select locations and offer "Click & Collect" from our Heidelberg Heights location in Melbourne. You can also book virtual consultations with our team. Visit our "Try TSU in Person" page for locations and booking information.'
      }
    ],
    products: [
      {
        id: 'prod-1',
        question: 'How do I choose the right size?',
        answer: 'We provide detailed size guides for each product, including measurements in centimeters and inches. We recommend measuring your body or the person who will wear the clothing and comparing with our size charts. If you\'re between sizes, we suggest sizing up for comfort. Our customer service team can also provide personalized sizing advice.'
      },
      {
        id: 'prod-2',
        question: 'What fabrics do you use in your clothing?',
        answer: 'We use a variety of high-quality, sensory-friendly fabrics including organic cotton, bamboo, modal, and specialized blends. All our fabrics are chosen for their softness, breathability, and durability. Many are tagless, seamless, and free from irritating textures. Fabric details are provided for each product on our website.'
      },
      {
        id: 'prod-3',
        question: 'Are your clothes machine washable?',
        answer: 'Yes, all our garments are designed for easy care. Most items are machine washable in cold water and can be tumble dried on low heat. We recommend avoiding fabric softeners and harsh detergents to maintain the quality and sensory properties of the fabrics. Specific care instructions are provided with each garment.'
      },
      {
        id: 'prod-4',
        question: 'Do you offer plus sizes?',
        answer: 'Yes, we offer extended sizing across many of our collections. Our size range typically extends up to 4XL, but availability varies by style. You can filter by size on our product pages. We\'re continuously expanding our size offerings based on customer feedback.'
      },
      {
        id: 'prod-5',
        question: 'What adaptive features do your clothes have?',
        answer: 'Our adaptive features include magnetic closures instead of buttons/zippers, open-back designs for easy dressing, adjustable fastenings, seamless construction, tagless labels, and strategically placed openings for medical device access. Each product listing specifies which adaptive features are included.'
      }
    ],
    ordering: [
      {
        id: 'order-1',
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit cards (Visa, Mastercard, American Express), PayPal, Apple Pay, Google Pay, and Afterpay for eligible orders. All payments are processed securely through encrypted channels. We also accept NDIS and HCP payments through invoice arrangements.'
      },
      {
        id: 'order-2',
        question: 'How long does shipping take?',
        answer: 'Australian orders typically arrive within 3-7 business days. Express shipping options are available for faster delivery. International shipping times vary from 7-21 business days depending on the destination. You\'ll receive tracking information once your order ships.'
      },
      {
        id: 'order-3',
        question: 'Do you offer express shipping?',
        answer: 'Yes, we offer express shipping options for both domestic and international orders. Express shipping is typically 1-3 business days within Australia and 3-7 business days internationally, depending on location. Shipping costs and options are calculated at checkout.'
      },
      {
        id: 'order-4',
        question: 'Can I modify or cancel my order after placing it?',
        answer: 'You can modify or cancel your order within 1 hour of placing it by contacting our customer service team. After this period, your order enters processing and cannot be changed. If you need to make changes after this time, please contact us immediately—we\'ll do our best to accommodate your request.'
      },
      {
        id: 'order-5',
        question: 'Do you ship to PO boxes?',
        answer: 'Yes, we ship to PO boxes within Australia. For international orders, shipping to PO boxes may be limited depending on the carrier. We recommend using a street address for faster and more reliable delivery, especially for express shipping.'
      }
    ],
    ndis: [
      {
        id: 'ndis-1',
        question: 'Are your products NDIS funded?',
        answer: 'Yes, many of our adaptive clothing items are eligible for NDIS funding under categories like "Assistive Technology" or "Daily Living Aids". The specific funding category depends on your NDIS plan and how the clothing supports your goals. We provide supporting documentation to assist with your funding applications.'
      },
      {
        id: 'ndis-2',
        question: 'How do I use my NDIS funding to purchase your products?',
        answer: 'You can use your NDIS funding by: 1) Requesting an invoice from us, 2) Submitting the invoice to your plan manager or NDIA, 3) Once approved, we\'ll process your order. We also work directly with plan managers and support coordinators. Contact our NDIS team for personalized assistance.'
      },
      {
        id: 'ndis-3',
        question: 'Do you accept Home Care Package (HCP) funds?',
        answer: 'Yes, we accept HCP funds for eligible customers. The process is similar to NDIS—request an invoice, submit it to your package manager, and once approved, we\'ll process your order. We have dedicated staff experienced in working with HCP providers.'
      },
      {
        id: 'ndis-4',
        question: 'Can you help with funding applications?',
        answer: 'Yes, we provide documentation and support letters to assist with funding applications. This includes product descriptions, how they address specific needs, and their benefits for independence and wellbeing. Contact our NDIS team for assistance with your application.'
      }
    ],
    care: [
      {
        id: 'care-1',
        question: 'How should I wash my adaptive clothing?',
        answer: 'We recommend washing in cold water (30°C or below) with mild detergent. Avoid fabric softeners and bleach. Turn garments inside out before washing. Tumble dry on low heat or line dry. For items with magnetic closures, avoid high heat which can damage the magnets.'
      },
      {
        id: 'care-2',
        question: 'Can I iron clothes with magnetic closures?',
        answer: 'Yes, but use low heat and avoid direct contact with the magnetic areas. We recommend using a pressing cloth between the iron and garment, or steaming instead of ironing. Always check the care label for specific instructions.'
      },
      {
        id: 'care-3',
        question: 'How do I maintain the magnetic closures?',
        answer: 'Magnetic closures are durable but should be cared for properly. Avoid exposing them to extreme heat, strong magnets, or dropping heavy objects on them. If magnets become weak, contact us—some closures have replaceable magnetic components.'
      }
    ],
    returns: [
      {
        id: 'return-1',
        question: 'What is your return policy?',
        answer: 'We offer a 30-day return policy for unworn, unwashed items with original tags attached. Items must be in original condition. Adaptive underwear and intimate apparel are final sale for hygiene reasons. Returns are processed within 5-10 business days of receiving the item.'
      },
      {
        id: 'return-2',
        question: 'How do I initiate a return?',
        answer: 'Log into your account, go to "My Orders", select the item to return, and follow the prompts. Alternatively, contact our customer service team with your order number. We\'ll provide a return authorization and instructions. Returns must be initiated within 30 days of delivery.'
      },
      {
        id: 'return-3',
        question: 'Who pays for return shipping?',
        answer: 'We provide free return shipping for faulty items or incorrect shipments. For change-of-mind returns, customers are responsible for return shipping costs. We offer a flat-rate return label for convenience. Refunds are issued to the original payment method.'
      }
    ],
    wholesale: [
      {
        id: 'ws-1',
        question: 'How can my business become a stockist?',
        answer: 'Complete our stockist application form on the website or email sales@theshapesunited.com with your business details. We require information about your business type, location, and customer base. Our wholesale team will review your application and contact you within 3 business days.'
      },
      {
        id: 'ws-2',
        question: 'What are the minimum order quantities?',
        answer: 'Minimum order quantities vary depending on the product category and partnership level. We offer flexible options for healthcare providers, retail stores, and institutions. Contact our wholesale team to discuss your specific needs and available options.'
      }
    ],
    technical: [
      {
        id: 'tech-1',
        question: 'Are your magnetic closures safe for people with pacemakers?',
        answer: 'Our magnetic closures use neodymium magnets that are generally safe for people with pacemakers when used as intended in clothing. However, we recommend consulting with your healthcare provider, as magnet strength and placement can vary. We can provide technical specifications for medical review.'
      },
      {
        id: 'tech-2',
        question: 'Can clothing be modified for specific medical devices?',
        answer: 'Yes, we offer customization services for specific medical device access needs. This includes creating openings for feeding tubes, dialysis ports, or monitoring equipment. Contact our custom solutions team to discuss your specific requirements.'
      }
    ]
  };

  const toggleQuestion = (questionId) => {
    setOpenQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const getFilteredFAQs = () => {
    if (!searchQuery.trim()) {
      return allFAQs[activeCategory] || [];
    }

    const query = searchQuery.toLowerCase();
    const allQuestions = Object.values(allFAQs).flat();
    
    return allQuestions.filter(faq => 
      faq.question.toLowerCase().includes(query) || 
      faq.answer.toLowerCase().includes(query)
    );
  };

  const faqsToShow = getFilteredFAQs();
  const hasSearchResults = searchQuery.trim() && faqsToShow.length > 0;
  const noSearchResults = searchQuery.trim() && faqsToShow.length === 0;

  return (
    <div className="faqs-page">
      {/* Hero Section */}
      <section className="faqs-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Frequently Asked Questions</h1>
            <p className="hero-subtitle">Find answers to common questions about The Shapes United</p>
            <p className="hero-text">
              Can't find what you're looking for? Contact our friendly support team for personalized assistance.
            </p>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="search-section">
        <div className="container">
          <div className="search-container">
            <div className="search-icon">🔍</div>
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button 
                className="clear-search"
                onClick={() => setSearchQuery('')}
              >
                Clear
              </button>
            )}
          </div>
          <p className="search-hint">Try searching for "sizing", "NDIS", "shipping", or "returns"</p>
        </div>
      </section>

      {/* Main Content */}
      <div className="faqs-main">
        <div className="container">
          <div className="faqs-layout">
            {/* Categories Sidebar */}
            {!searchQuery && (
              <div className="categories-sidebar">
                <h3 className="sidebar-title">Browse by Category</h3>
                <div className="categories-list">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                      onClick={() => setActiveCategory(category.id)}
                    >
                      <span className="category-icon">{category.icon}</span>
                      <span className="category-name">{category.name}</span>
                      <span className="question-count">
                        {(allFAQs[category.id] || []).length}
                      </span>
                    </button>
                  ))}
                </div>
                
                <div className="sidebar-help">
                  <h4>Need More Help?</h4>
                  <p>Can't find your answer here? Our team is ready to assist you.</p>
                  <a href="/contact" className="help-link">
                    Contact Support Team →
                  </a>
                </div>
              </div>
            )}

            {/* FAQs Content */}
            <div className="faqs-content">
              {hasSearchResults && (
                <div className="search-results-header">
                  <h2>Search Results for "{searchQuery}"</h2>
                  <p className="results-count">{faqsToShow.length} results found</p>
                </div>
              )}

              {!searchQuery && (
                <div className="category-header">
                  <div className="category-info">
                    <span className="current-category-icon">
                      {categories.find(c => c.id === activeCategory)?.icon}
                    </span>
                    <div>
                      <h2>{categories.find(c => c.id === activeCategory)?.name}</h2>
                      <p className="category-description">
                        {activeCategory === 'general' && 'General information about The Shapes United'}
                        {activeCategory === 'products' && 'Product details, sizing, and features'}
                        {activeCategory === 'ordering' && 'Order placement, payment, and shipping'}
                        {activeCategory === 'ndis' && 'NDIS funding and financial support'}
                        {activeCategory === 'care' && 'Care instructions and maintenance'}
                        {activeCategory === 'returns' && 'Returns, exchanges, and refunds'}
                        {activeCategory === 'wholesale' && 'Wholesale partnerships and stockists'}
                        {activeCategory === 'technical' && 'Technical specifications and adaptations'}
                      </p>
                    </div>
                  </div>
                  <div className="category-stats">
                    <span className="stat">
                      <span className="stat-number">{(allFAQs[activeCategory] || []).length}</span>
                      <span className="stat-label">Questions</span>
                    </span>
                  </div>
                </div>
              )}

              {noSearchResults ? (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <h3>No results found for "{searchQuery}"</h3>
                  <p>Try different keywords or browse our categories for related questions.</p>
                  <button 
                    className="browse-categories-btn"
                    onClick={() => setSearchQuery('')}
                  >
                    Browse All Categories
                  </button>
                </div>
              ) : (
                <div className="questions-list">
                  {faqsToShow.map(faq => (
                    <div 
                      key={faq.id} 
                      className={`question-item ${openQuestions.includes(faq.id) ? 'open' : ''}`}
                    >
                      <div 
                        className="question-header"
                        onClick={() => toggleQuestion(faq.id)}
                      >
                        <h3 className="question-title">{faq.question}</h3>
                        <span className="toggle-icon">
                          {openQuestions.includes(faq.id) ? '−' : '+'}
                        </span>
                      </div>
                      
                      <div className="question-answer">
                        <p>{faq.answer}</p>
                        
                        {/* Additional actions for specific questions */}
                        {(faq.id === 'gen-5' || faq.id.includes('stores')) && (
                          <div className="answer-actions">
                            <a href="/try-tsu" className="action-link">
                              Find Store Locations
                            </a>
                            <a href="/contact" className="action-link">
                              Book Virtual Consultation
                            </a>
                          </div>
                        )}
                        
                        {(faq.id === 'ndis-2' || faq.id.includes('ndis')) && (
                          <div className="answer-actions">
                            <a href="/older-adults" className="action-link">
                              Learn About NDIS Process
                            </a>
                            <a href="/contact" className="action-link">
                              Contact NDIS Team
                            </a>
                          </div>
                        )}
                        
                        {(faq.id === 'ws-1' || faq.id.includes('wholesale')) && (
                          <div className="answer-actions">
                            <a href="/try-tsu#stockist" className="action-link">
                              Become a Stockist
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick Links */}
              {!searchQuery && (
                <div className="quick-links">
                  <h3>Quick Help Topics</h3>
                  <div className="quick-links-grid">
                    <a href="/size-guide" className="quick-link">
                      <span className="link-icon">📏</span>
                      <span className="link-text">Size Guide</span>
                    </a>
                    <a href="/shipping" className="quick-link">
                      <span className="link-icon">🚚</span>
                      <span className="link-text">Shipping Information</span>
                    </a>
                    <a href="/returns" className="quick-link">
                      <span className="link-icon">🔄</span>
                      <span className="link-text">Returns Policy</span>
                    </a>
                    <a href="/ndis" className="quick-link">
                      <span className="link-icon">💰</span>
                      <span className="link-text">NDIS Funding Guide</span>
                    </a>
                    <a href="/care-guide" className="quick-link">
                      <span className="link-icon">🧼</span>
                      <span className="link-text">Care Instructions</span>
                    </a>
                    <a href="/contact" className="quick-link">
                      <span className="link-icon">💬</span>
                      <span className="link-text">Contact Support</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <section className="contact-cta">
        <div className="container">
          <div className="cta-content">
            <div className="cta-icon">💬</div>
            <div className="cta-text">
              <h2>Still Have Questions?</h2>
              <p>Our support team is here to help you find the perfect adaptive clothing solution.</p>
            </div>
            <div className="cta-actions">
              <a href="/contact" className="cta-btn primary">
                Contact Support
              </a>
              <a href="tel:1300000000" className="cta-btn secondary">
                Call Now: 1300 000 000
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Video Tutorials */}
      <section className="tutorials-section">
        <div className="container">
          <div className="section-header">
            <h2>Helpful Video Guides</h2>
            <p className="section-subtitle">Watch our tutorials for visual guidance</p>
          </div>
          
          <div className="tutorials-grid">
            <div className="tutorial-card">
              <div className="tutorial-thumbnail">
                <div className="play-button">▶</div>
              </div>
              <div className="tutorial-content">
                <h3>How to Use Magnetic Closures</h3>
                <p>A step-by-step guide to using our adaptive fastenings</p>
                <div className="tutorial-meta">
                  <span className="duration">2:15</span>
                  <span className="views">1.2K views</span>
                </div>
              </div>
            </div>
            
            <div className="tutorial-card">
              <div className="tutorial-thumbnail">
                <div className="play-button">▶</div>
              </div>
              <div className="tutorial-content">
                <h3>Measuring for Perfect Fit</h3>
                <p>How to accurately measure for our adaptive clothing</p>
                <div className="tutorial-meta">
                  <span className="duration">3:45</span>
                  <span className="views">2.1K views</span>
                </div>
              </div>
            </div>
            
            <div className="tutorial-card">
              <div className="tutorial-thumbnail">
                <div className="play-button">▶</div>
              </div>
              <div className="tutorial-content">
                <h3>NDIS Funding Process</h3>
                <p>Navigating NDIS funding for adaptive clothing</p>
                <div className="tutorial-meta">
                  <span className="duration">4:30</span>
                  <span className="views">1.8K views</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="faqs-newsletter">
        <div className="container">
          <div className="newsletter-content">
            <h2>Stay Updated</h2>
            <p>Subscribe to receive updates on new FAQs, product guides, and adaptive fashion tips.</p>
            
            <form className="newsletter-form">
              <div className="form-inputs">
                <input type="email" placeholder="Your email address" />
                <button type="submit" className="subscribe-btn">
                  Subscribe
                </button>
              </div>
              <p className="form-note">We respect your privacy. Unsubscribe anytime.</p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FAQs;